import { Box } from "@mui/material";
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  accountStatus,
  callAPIInterface,
  handleContactSupport,
  restrictedModulesList,
} from "../constants";
import {
  accountStatusMsg,
  accountStatus as accountStatusTxt,
} from "../messages";
import Text from "../text/Text";
import { Tag } from "../tag/Tag";
import CommonSkeleton from "../CommonSkeleton";
import CustomDivider from "../divider/Divider";
import CommonHeader from "../CommonHeader";
import Button from "../button/Button";
import UpgradePlanDrawer from "./limit/UpgradePlanDrawer";

const AccountStatus = () => {
  const navigate = useNavigate();
  const [accountRestrictions, setAccountRestrictions] = useState(
    restrictedModulesList
  );
  const [openUpgradeModal, setOpenUpgradeModal] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    getAccountRestrictions();
  }, []);

  const getAccountRestrictions = async () => {
    try {
      setIsLoading(true);
      const response = await callAPIInterface("GET", "/account-restrictions");
      const restrictions = response?.account_restrictions || [];

      let config = { reasons: {}, actions: {} };
      if (restrictions.length > 0) {
        try {
          const data = await callAPIInterface(
            "GET",
            "/account-restrictions/configurations"
          );

          const reasons = data?.reasons || [];
          const actions = data?.actions || [];

          reasons.forEach((reason) => (config.reasons[reason.id] = reason));
          actions.forEach((action) => (config.actions[action.id] = action));
        } catch (error) {
          console.error(error);
        }
      }

      const newModulesList = [];
      restrictedModulesList.forEach((staticModule) => {
        const rs = restrictions
          .map((r) => {
            if (r.module === staticModule.module) {
              const reason = config.reasons[r.speed_reason_code];
              const actions = (r.action_ids || [])
                .map((id) => config.actions[id])
                .filter(Boolean);

              return {
                reason: reason?.display_name || r.reason,
                error_message: reason?.error_message || r.error_message,
                actions: actions,
                display_name: r.display_name,
              };
            }

            return null;
          })
          .filter(Boolean);

        if (rs.length > 0) {
          newModulesList.push({
            ...staticModule,
            name: rs[0].display_name || staticModule.name,
            is_restricted: true,
            restrictionDetails: rs,
          });
        } else {
          newModulesList.push({
            ...staticModule,
            is_restricted: false,
          });
        }
      });

      setAccountRestrictions(newModulesList);
      setIsLoading(false);
    } catch (err) {
      setIsLoading(false);
    }
  };

  const handleAction = (action) => {
    switch (action.name) {
      case "PREMIUM_PLAN_PURCHASE":
        setOpenUpgradeModal(true);
        break;
      case "PHONE_VERIFICATION":
        navigate("/phone-number-verification");
        break;
      default:
        handleContactSupport();
        break;
    }
  };

  return (
    <>
      <CommonHeader textAfterIcon={accountStatusTxt} icon="backArrow" />
      <Box className="options-container">
        <Box className="account-status-wrapper">
          <Box className="module-container">
            <Text className="grey-text" size={16} font="regular">
              {accountStatusMsg}
            </Text>
            {accountRestrictions.map((module, index) => {
              const isRestrictedModule =
                JSON.parse(process.env.REACT_APP_LIVEMODE) &&
                module.is_restricted;

              return isLoading ? (
                <Box
                  key={index}
                  className="account-modules"
                  display="flex"
                  justifyContent="space-between"
                  sx={{ backgroundColor: "#f5f4f4" }}
                >
                  <CommonSkeleton />
                  <CommonSkeleton />
                </Box>
              ) : (
                <Box
                  key={index}
                  className="account-modules"
                  sx={{
                    backgroundColor: isRestrictedModule ? "#fefbf1" : "#f7fafc",
                  }}
                >
                  <Box
                    display="flex"
                    justifyContent="space-between"
                    sx={{ gap: "4px" }}
                  >
                    <Box>
                      <Text
                        className="default-text"
                        size={16}
                        font="bold"
                        sx={{ display: "flex", alignItems: "center" }}
                      >
                        {module.name}
                      </Text>

                      {isRestrictedModule && (
                        <Text
                          className="grey-text"
                          size={14}
                          font="regular"
                          color="#6b7280"
                        >
                          {module.restrictionDetails?.length} issue
                          {module.restrictionDetails?.length > 1
                            ? "s"
                            : ""}{" "}
                          need your attention
                        </Text>
                      )}
                    </Box>
                    <Tag
                      text={
                        isRestrictedModule
                          ? accountStatus.restricted.label
                          : accountStatus.active.label
                      }
                      variant={
                        isRestrictedModule
                          ? accountStatus.restricted.variant
                          : accountStatus.active.variant
                      }
                    />
                  </Box>

                  {isRestrictedModule && (
                    <>
                      <CustomDivider
                        sx={{ borderColor: "#F6F3F3", margin: "16px 0" }}
                      />
                      <Box className="action-container">
                        {module.restrictionDetails?.map((mod, idx) => (
                          <Box
                            key={idx}
                            className="module-reason-action-container"
                            justifyContent="space-between"
                          >
                            <Box className="reason-text-container">
                              <Text
                                className="default-text"
                                size={14}
                                font="semibold"
                              >
                                {mod.reason}
                              </Text>
                              <Text
                                className="grey-text"
                                size={14}
                                font="regular"
                                sx={{ marginTop: "4px" }}
                              >
                                {mod.error_message}
                              </Text>
                            </Box>

                            {mod.actions?.length > 0 && (
                              <>
                                <CustomDivider
                                  sx={{
                                    borderColor:
                                      "var(--Account-Status-Border, #FCF3CF)",
                                    margin: "16px 0",
                                  }}
                                />
                                <Box className="action-btn-container">
                                  <Text
                                    className="grey-text"
                                    size={14}
                                    font="semibold"
                                    sx={{ marginTop: "4px" }}
                                  >
                                    Take an action{" "}
                                    {mod?.actions?.length > 1
                                      ? `(any one)`
                                      : ""}
                                  </Text>

                                  <Box className="action-button">
                                    {mod.actions.map((action, actionIdx) => (
                                      <Button
                                        key={actionIdx}
                                        variant="outlined"
                                        className="submit-btn"
                                        label={action.display_name}
                                        icon="KeyboardArrowRightOutlined"
                                        sx={{ width: "48%" }}
                                        onClick={() => handleAction(action)}
                                      />
                                    ))}
                                  </Box>
                                </Box>
                              </>
                            )}
                          </Box>
                        ))}
                      </Box>
                    </>
                  )}
                </Box>
              );
            })}
          </Box>
        </Box>
        {openUpgradeModal && (
          <UpgradePlanDrawer
            openModal={openUpgradeModal}
            setOpenModal={setOpenUpgradeModal}
          />
        )}
      </Box>
    </>
  );
};

export default AccountStatus;
